// Number/date helpers for the stats pages. Every function takes an optional
// www locale; without one it formats exactly as before (en-US), which is what
// StatsCard on /apps/<id> still uses.

const compactCache = new Map<string, Intl.NumberFormat>();
const fullCache = new Map<string, Intl.NumberFormat>();

function compact(locale: string): Intl.NumberFormat {
  let f = compactCache.get(locale);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    });
    compactCache.set(locale, f);
  }
  return f;
}

function full(locale: string): Intl.NumberFormat {
  let f = fullCache.get(locale);
  if (!f) {
    f = new Intl.NumberFormat(locale);
    fullCache.set(locale, f);
  }
  return f;
}

/** 12.3K style for tables/tiles; exact below 10,000. */
export function formatCount(
  value: number | null | undefined,
  locale = "en-US",
): string {
  if (value === null || value === undefined) return "–";
  return value < 10_000
    ? full(locale).format(value)
    : compact(locale).format(value);
}

export function formatExact(
  value: number | null | undefined,
  locale = "en-US",
): string {
  if (value === null || value === undefined) return "–";
  return full(locale).format(value);
}

export function formatPercent(
  value: number | null | undefined,
  locale?: string,
): string {
  if (value === null || value === undefined) return "–";
  if (!locale) return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
  return new Intl.NumberFormat(locale, {
    style: "percent",
    signDisplay: "exceptZero",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
}

export function formatDate(
  unixSeconds: number | null | undefined,
  locale = "en-US",
): string {
  if (!unixSeconds) return "–";
  return new Date(unixSeconds * 1000).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function formatDateTime(unixSeconds: number, locale = "en-US"): string {
  return new Date(unixSeconds * 1000).toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
}

/**
 * "3 days ago" — rendered server-side, so it is relative to the render time.
 * Without a locale (or "en") it keeps the English wording; any other locale
 * uses Intl.RelativeTimeFormat ("vor 3 Tagen").
 */
export function formatAgo(
  unixSeconds: number | null | undefined,
  locale?: string,
): string {
  if (!unixSeconds) return "–";
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  const days = Math.floor(diff / 86400);
  if (!locale || locale === "en") {
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    if (days < 60) return `${days} day${days === 1 ? "" : "s"} ago`;
    return formatDate(unixSeconds);
  }
  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: "always",
    style: "short",
  });
  if (diff < 3600)
    return rtf.format(-Math.max(1, Math.floor(diff / 60)), "minute");
  if (diff < 86400) return rtf.format(-Math.floor(diff / 3600), "hour");
  if (days < 60) return rtf.format(-days, "day");
  return formatDate(unixSeconds, locale);
}

/** Positive-review share, e.g. 94.6. */
export function reviewScore(
  reviews: number | null,
  positive: number | null,
): number | null {
  if (!reviews || positive === null) return null;
  return Math.round((positive / reviews) * 1000) / 10;
}
